// Pure helpers for the "Send commission report" (Email PDF) action on
// the Admin Team page. JSX-free so tests import without a DOM.
//
// The action emails an advisor their most recent commission-report PDF
// from the acting admin's Outlook mailbox, via the existing audited
// endpoint POST /api/admin/commission/reports/:id/send. It is offered
// only when the advisor actually has a generated report — otherwise the
// row/bulk control is hidden with a plain reason (no dead end).

import type { TeamRow, TeamUserLike } from "./admin-team-rows";

// The user fields this module reads. AdminTeamUser (the real row type) is
// a structural superset, so a TeamRow<AdminTeamUser> assigns cleanly.
export interface CommissionUserLike extends TeamUserLike {
  latest_commission_report_id: string | null;
  latest_commission_period: string | null;
}

export interface CommissionSendInfo {
  available: boolean;
  reportId: string | null;
  period: string | null;
  /** Plain-English reason when not available (for tooltips / empty UI). */
  reason: string | null;
}

/** Whether this row can have its latest commission report emailed, and
 *  which report. Only active advisor rows with a PDF-backed report
 *  qualify; inactive accounts and rows with no report do not. */
export function commissionSendInfo(
  row: TeamRow<CommissionUserLike>,
): CommissionSendInfo {
  if (!row.user) {
    return { available: false, reportId: null, period: null, reason: null };
  }
  if (row.status !== "active") {
    return {
      available: false,
      reportId: null,
      period: null,
      reason: "Account is not active",
    };
  }
  const reportId = row.user.latest_commission_report_id;
  if (!reportId) {
    return {
      available: false,
      reportId: null,
      period: row.user.latest_commission_period,
      reason: "No commission report generated yet",
    };
  }
  return {
    available: true,
    reportId,
    period: row.user.latest_commission_period,
    reason: null,
  };
}

/** "2026-05" → "May 2026". Returns the raw value if it isn't a
 *  YYYY-MM string. */
export function formatCommissionPeriod(period: string | null): string {
  if (!period) return "";
  const match = /^(\d{4})-(\d{2})$/.exec(period);
  if (!match) return period;
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const monthName = months[monthIndex];
  if (!monthName) return period;
  return `${monthName} ${year}`;
}

/** Rows in a selection that can actually be sent (active + have a
 *  report). Used to gate the bulk "Send commission report" action. */
export function sendableRows(
  rows: TeamRow<CommissionUserLike>[],
): TeamRow<CommissionUserLike>[] {
  return rows.filter((r) => commissionSendInfo(r).available);
}
